import React, { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn, getSouthAfricanDate } from '@/lib/utils';
import { Product } from '@/types';
import { haptic } from '@/lib/haptics';
import { getCategoryIcon } from '@/lib/constants';

interface RestockFormProps {
  products: Product[];
  onSubmit: (data: { productId: string; quantity: number; supplier?: string; notes?: string; date: string }) => void;
  isLoading?: boolean;
  onAddNewProduct?: () => void;
  selectedProductId?: string | null;
  onProductSelected?: () => void;
}

export function RestockForm({ products, onSubmit, isLoading, onAddNewProduct, selectedProductId, onProductSelected }: RestockFormProps) {
  const today = getSouthAfricanDate(0);
  const [formData, setFormData] = useState({
    productId: '',
    quantity: 0,
    supplier: '',
    notes: '',
    date: today, // Default to today
  });

  // Auto-select the newly created product
  React.useEffect(() => {
    if (selectedProductId && products.find(p => p.id === selectedProductId)) {
      setFormData(prev => ({ ...prev, productId: selectedProductId }));
      onProductSelected?.();
    }
  }, [selectedProductId, products, onProductSelected]);

  const selectedProduct = products.find(p => p.id === formData.productId);
  const isHistorical = formData.date !== today;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      productId: formData.productId,
      quantity: formData.quantity,
      supplier: formData.supplier || undefined,
      notes: formData.notes || undefined,
      date: formData.date,
    });
    // Reset form (keep date selection)
    setFormData(prev => ({ productId: '', quantity: 0, supplier: '', notes: '', date: prev.date }));
  };

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <form onSubmit={handleSubmit} className="bg-dark-card rounded-2xl p-5 border-2 border-primary/30 shadow-lg shadow-primary/5 space-y-5">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-gradient-gold flex items-center justify-center text-2xl shadow-lg shadow-primary/20">
          📥
        </div>
        <div>
          <h2 className="text-xl font-black text-white">Record Restock</h2>
          <p className="text-sm text-gray-400">Add inventory to your stock</p>
        </div>
      </div>

      {/* Product Selector - Premium */}
      <div>
        <label className="block text-sm font-semibold text-gray-200 mb-2">
          Select Product
        </label>
        <div className="relative">
          <select
            value={formData.productId}
            onChange={(e) => {
              haptic.select();
              if (e.target.value === '__add_new__') {
                onAddNewProduct?.();
              } else {
                handleChange('productId', e.target.value);
              }
            }}
            className={cn(
              'w-full px-4 py-3 min-h-[48px] bg-dark-lighter border-2 border-gray-700/50 rounded-xl shadow-sm',
              'text-white text-base',
              'focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-200',
              'appearance-none cursor-pointer'
            )}
            required
          >
            <option value="">Choose a product...</option>
            {onAddNewProduct && (
              <option value="__add_new__" className="text-primary font-bold">
                ➕ Add New Product
              </option>
            )}
            {products.map((product) => (
              <option key={product.id} value={product.id}>
                {getCategoryIcon(product.category)} {product.name} ({product.currentStock.toFixed(1)} {product.unitType})
              </option>
            ))}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-400 text-lg">
            ▼
          </div>
        </div>
      </div>

      {/* Date Selector - Premium */}
      <div>
        <label className="block text-sm font-semibold text-gray-200 mb-2">
          Restock Date
        </label>
        <input
          type="date"
          value={formData.date}
          onChange={(e) => handleChange('date', e.target.value)}
          max={today}
          className={cn(
            'w-full px-4 py-3 min-h-[48px] bg-dark-lighter border-2 border-gray-700/50 rounded-xl shadow-sm',
            'text-white text-base',
            'focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-200'
          )}
          required
        />
        {isHistorical && (
          <div className="mt-3 px-4 py-3 bg-warning/10 border-2 border-warning/30 rounded-xl">
            <p className="text-sm text-warning flex items-center gap-2 font-medium">
              <span className="text-lg">⚠️</span>
              <span>Historical restock - Current stock levels will NOT be automatically updated</span>
            </p>
          </div>
        )}
      </div>

      {/* Selected Product Display - Premium Card */}
      {selectedProduct && (
        <div className="bg-gradient-to-br from-dark-lighter to-dark rounded-xl p-4 border-2 border-primary/40 shadow-lg">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-dark-card border-2 border-gray-700/50 flex items-center justify-center text-3xl">
              {getCategoryIcon(selectedProduct.category)}
            </div>
            <div className="flex-1">
              <div className="font-bold text-white text-lg">{selectedProduct.name}</div>
              <div className="text-sm text-gray-400">
                Current stock: <span className="text-primary font-semibold">{selectedProduct.currentStock.toFixed(1)}</span> {selectedProduct.unitType}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quantity Input - Premium with Preview */}
      <div>
        <label className="block text-sm font-semibold text-gray-200 mb-2">
          Quantity {selectedProduct && `(${selectedProduct.unitType})`}
        </label>
        <Input
          type="number"
          step="0.1"
          min="0"
          value={formData.quantity || ''}
          onChange={(e) => handleChange('quantity', parseFloat(e.target.value) || 0)}
          placeholder={selectedProduct ? `Enter amount in ${selectedProduct.unitType}` : 'Select a product first'}
          disabled={!selectedProduct}
          required
        />
        {selectedProduct && formData.quantity > 0 && !isHistorical && (
          <div className="mt-3 px-4 py-3 bg-success/10 border-2 border-success/30 rounded-xl">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-300">New stock will be:</span>
              <span className="text-xl font-black text-success">
                {(selectedProduct.currentStock + formData.quantity).toFixed(1)} {selectedProduct.unitType}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Supplier Field */}
      <Input
        label="Supplier (Optional)"
        value={formData.supplier}
        onChange={(e) => handleChange('supplier', e.target.value)}
        placeholder="e.g. Green Valley Farms"
        hint="Track where your inventory comes from"
      />

      {/* Notes Field - Premium */}
      <div>
        <label className="block text-sm font-semibold text-gray-200 mb-2">
          Notes (Optional)
        </label>
        <textarea
          value={formData.notes}
          onChange={(e) => handleChange('notes', e.target.value)}
          placeholder="Any additional details..."
          rows={2}
          className={cn(
            'w-full px-4 py-3 min-h-[80px] bg-dark-lighter border-2 border-gray-700/50 rounded-xl shadow-sm',
            'text-white text-base placeholder-gray-500',
            'focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-200',
            'resize-none'
          )}
        />
      </div>

      {/* Submit Button - Full Width Premium */}
      <Button
        type="submit"
        variant="success"
        size="lg"
        disabled={isLoading || !formData.productId || formData.quantity <= 0}
        isLoading={isLoading}
        className="w-full"
        icon={<span className="text-xl">📥</span>}
      >
        {formData.quantity > 0 ? `Record Restock (+${formData.quantity.toFixed(1)})` : 'Record Restock'}
      </Button>
    </form>
  );
}
